import React from "react";
import PropTypes from "prop-types";
import { controlPropTypes } from "../formhelpers";
import InputMask from "./InputMask/InputMask";
import { processKeyName } from "./utils/keyEvents";

export const ENTER_KEY = "Enter";
export const INPUT_VALUE_TYPES = {
    INTEGER: "integer",
    DECIMAL: "decimal",
    STRING: "string",
};

const checkIntegerKey = (rawValue, key, index, type = INPUT_VALUE_TYPES.INTEGER) => {
    if (key === "-") { // ignore negative numbers.
        if (rawValue.indexOf("-") > 0) {
            // only allow one negative sign
            return `Invalid character "a 2nd negative" was entered for this ${type} field and will be ignored.`;
        }
        if (index > 0) {
            // only allow one negative sign
            return `Invalid character position "negative" was entered for this ${type} field and will be ignored.`;
        }
    }
    else if(
        (key < "0" || key > "9") && // ignore 0-9
        (key.length === 1) // ignore named keys.
    ) {
        return `Invalid character "${key}" was entered for this ${type} field and will be ignored.`;
    }
};

const checkDecimalKey = (rawValue, key, index) => {
    if (key === ".") {
        if (rawValue.indexOf(".") >= 0) {
            // only allow one decimal point
            return `Invalid character "a 2nd decimal" was entered for this decimal field and will be ignored.`;
        }
    }
    else {
        return checkIntegerKey(rawValue, key, index, INPUT_VALUE_TYPES.DECIMAL);
    }
};

const validateInsertText = (valueType, currentValue, selectionStart, selectionEnd, insertValue) => {
    // returns
    //   false - no errors present.
    //   {msg,value} - error occurred, returns valid value up to error point.
    const checkKey = valueType === INPUT_VALUE_TYPES.INTEGER
        ? checkIntegerKey
        : valueType === INPUT_VALUE_TYPES.DECIMAL
            ? checkDecimalKey
            : null;
    if (checkKey == null) return false; // no rules to validate.
    let nextValue = currentValue;
    if(selectionStart === undefined) {
        // browser does not expose e.target.selectionStart/End?
        // paste to end of value
        selectionEnd = selectionStart = currentValue.length;
    }
    // insert the clipboard text into the value replacing the selectionStart/End contents.
    nextValue = nextValue.slice(0, selectionStart) + insertValue + nextValue.slice(selectionEnd);

    let value = "";
    for (let i = 0; i < nextValue.length; i++) {
        const c = nextValue.charAt(i);
        const msg = checkKey(value, c, i);
        if (msg != null) {
            return {
                msg,
                value
            };
        }
        value += c;
    }
    return false;
};

export default class Input extends React.Component {

    constructor(props) {
        super(props);
        this.inputRef = props._ref || React.createRef();
        /* Some controls will require a local state so editing can take place
         * before the final value is actually committed.
         */
        this.state = {
            value: this.convertValueToString(props.value)
        };
        this.hasChanges = false; // this is used so onBlur only commits when changes have been made.
        this.hasFocus = false; // only commit change when control has focus.
        this.onFocus = this.onFocus.bind(this);
        this.onBlur = this.onBlur.bind(this);
        this.onClick = this.onClick.bind(this);
        this.onChange = this.onChange.bind(this);
        this.onKeyDown = this.onKeyDown.bind(this);
        this.onPaste = this.onPaste.bind(this);
    }

    convertStringToValue(strValue, checkForChanged = true) {
        const {valueType, value} = this.props;
        if(checkForChanged && !this.hasChanges) {
            // just return the prop value
            return value;
        }
        if(strValue === "") {
            return null;
        }
        switch(valueType) {
            case INPUT_VALUE_TYPES.DECIMAL:
                if (strValue === ".") return "0.";
                return isNaN(parseFloat(strValue)) ? null : strValue;
            case INPUT_VALUE_TYPES.INTEGER:
                strValue = parseInt(strValue, 10);
                return !isNaN(strValue) ? strValue : null;
            case INPUT_VALUE_TYPES.STRING:
            default:
                return strValue;
        }
    }

    convertValueToString(value) {
        const {valueType} = this.props;
        if(value === undefined || value === null) {
            return "";
        }
        switch(valueType) {
            case INPUT_VALUE_TYPES.DECIMAL:
                return value.toString();
            case INPUT_VALUE_TYPES.INTEGER:
                return value.toString();
            case INPUT_VALUE_TYPES.STRING:
            default:
                return value;
        }
    }

    onKeyDown(e) {
        const { descriptor, onKeyDown, value, onCommitChange, enabled, valueType, onIllegal } = this.props;
        const rawValue = e.target.value;
        const newValue = this.convertStringToValue(rawValue);
        const key = processKeyName(e.key);
        if (key === ENTER_KEY) { // enter
            if(newValue !== value) {
                // commit the value change
                enabled && onCommitChange(descriptor, newValue);
                this.hasChanges = false;
            }
        }
        else {
            const isModifier = ( e.ctrlKey || e.altKey || e.metaKey || key.length > 1);
            if(!isModifier) {
                const result = validateInsertText(
                    valueType,
                    rawValue,
                    e.target.selectionStart,
                    e.target.selectionEnd,
                    key
                );
                if(result) {
                    // validation failed
                    e.preventDefault && e.preventDefault();
                    e.stopPropagation && e.stopPropagation();
                    onIllegal(result.msg);
                }
            }
        }
        onKeyDown && onKeyDown(e, descriptor, newValue, false);
    }

    onPaste(e) {
        const { valueType, onIllegal } = this.props;
        const result = validateInsertText(
                valueType,
                e.target.value,
                e.target.selectionStart,
                e.target.selectionEnd,
                e.clipboardData.getData("Text")
            );
        if(result) {
            // validation failed
            e.preventDefault && e.preventDefault();
            e.stopPropagation && e.stopPropagation();
            this.setState({value: result.value});
            this.hasChanges = true;
            onIllegal(result.msg);
        }
    }

    onClick(e) {
        const { descriptor, onClick } = this.props;
        onClick && onClick(e, descriptor, this.convertStringToValue(e.target.value));
    }

    onFocus(e) {
        const { descriptor, onFocus } = this.props;
        onFocus && onFocus(e, descriptor, this.convertStringToValue(e.target.value));
        this.hasFocus = true;
    }

    onBlur(e) {
        const { descriptor, onBlur, value, enabled, onCommitChange, focused } = this.props;
        if(!this.hasFocus && !focused) {
            // control does not have focus.
            return;
        }
        const newValue = this.convertStringToValue(e.target.value);
        if(newValue !== value) {
            // commit the value change
            enabled && onCommitChange(descriptor, newValue);
        }
        this.hasChanges = false;
        // trigger blur after onCommitChange has fired.
        enabled && onBlur && onBlur(e, descriptor, newValue);
        this.hasFocus = false;
    }

    UNSAFE_componentWillReceiveProps(nextProps) {
        // if the control has focus and has changes then don't change the local state.
        // the user is most editing a while records are updating in the background.
        if(this.hasChanges && this.hasFocus) return;

        /* Make sure the local state stays in sync with the passed in props. */
        const nextPropValue = this.convertValueToString(nextProps.value);
        if (this.state.value !== nextPropValue || this.inputRef.current.value !== nextPropValue) {
            // only update state if the prop values do not match
            // otherwise let the user continue their edits.
            this.setState({value: nextPropValue});
            this.hasChanges = false;
        }
    }

    onChange(e) {
        this.setState({value: e.target.value});
        this.hasChanges = true;
    }

    render() {
        const { className, descriptor, placeholder, enabled, style = {}, maxLength, maskFormat } = this.props;
        if(maxLength) {
            // when length is provided resize width of input
            style.width = (parseFloat(maxLength.toString()) * 0.8) + 2.0 + "em";
        }
        const value = this.state.value || "";
        const classname = (className || "form-control") + " focusable";
        return (<InputMask
            type="input"
            _ref={this.inputRef}
            className={classname}
            onClick={this.onClick}
            onChange={this.onChange}
            onFocus={this.onFocus}
            onBlur={this.onBlur}
            onKeyDown={this.onKeyDown}
            maskFormat={maskFormat}
            value={value}
            maxLength={maxLength}
            placeholder={placeholder ? descriptor.label : ""}
            disabled={!enabled}
            onPasteCapture={this.onPaste}
            style={style}
        />);

    }
}

Input.propTypes = {
    ...controlPropTypes(),
    onIllegal: PropTypes.func,
};

Input.defaultProps = {
    onIllegal: () => { /* noop */ },
};



// WEBPACK FOOTER //
// ./app/lib/common/descriptorcontrols/BaseControls/Input.js